import { useState } from 'react';
import clsx from 'clsx';
import type { Draft, GroupingQ } from '../types';
import styles from '../QuizView.module.css';

type Props = {
  question: GroupingQ;
  draft: Extract<Draft, { format: 'grouping' }>;
  onChange: (d: Extract<Draft, { format: 'grouping' }>) => void;
  disabled: boolean;
  showFeedback: boolean;
};

export function Grouping({ question, draft, onChange, disabled, showFeedback }: Props) {
  const [selected, setSelected] = useState<number | null>(null);

  const assign = (itemIdx: number, category: string | null) => {
    const next = draft.assignments.slice();
    next[itemIdx] = category;
    onChange({ ...draft, assignments: next });
  };

  const clickItem = (itemIdx: number) => {
    if (disabled) return;
    if (draft.assignments[itemIdx]) {
      // Already placed → click to send it back to the pool.
      assign(itemIdx, null);
      return;
    }
    setSelected(selected === itemIdx ? null : itemIdx);
  };

  const clickBucket = (cat: string) => {
    if (disabled || selected === null) return;
    assign(selected, cat);
    setSelected(null);
  };

  const correctCategoryOf = (item: string): string | undefined =>
    question.groups.find((g) => g.items.includes(item))?.category;

  return (
    <div className={styles.grouping}>
      {question.instruction && (
        <p className={styles.qInstruction}>{question.instruction}</p>
      )}

      <div className={styles.buckets}>
        {question.groups.map((g) => {
          const inBucket = draft.items
            .map((item, i) => ({ item, i }))
            .filter(({ i }) => draft.assignments[i] === g.category);
          return (
            <button
              key={g.category}
              type="button"
              onClick={() => clickBucket(g.category)}
              className={clsx(
                styles.bucket,
                selected !== null && !showFeedback && styles.bucketActive
              )}
              disabled={disabled || selected === null}
              aria-disabled={selected === null}
            >
              <span className={styles.bucketTitle}>{g.category}</span>
              <ul className={styles.bucketContents}>
                {inBucket.map(({ item, i }) => {
                  const correct = correctCategoryOf(item) === g.category;
                  return (
                    <li
                      key={i}
                      className={clsx(
                        styles.bucketItem,
                        showFeedback && correct && styles.bucketItemOk,
                        showFeedback && !correct && styles.bucketItemKo
                      )}
                    >
                      {item}
                    </li>
                  );
                })}
              </ul>
            </button>
          );
        })}
      </div>

      {!showFeedback && (
        <div className={styles.pool}>
          {draft.items.map((item, i) =>
            draft.assignments[i] !== null ? null : (
              <button
                key={i}
                type="button"
                onClick={() => clickItem(i)}
                className={clsx(styles.chip, selected === i && styles.chipSelected)}
                disabled={disabled}
                aria-pressed={selected === i}
              >
                {item}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
